import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { MaskLines, introDelay, SOFT } from "@/components/site/Motion";
import { RotatingBadge } from "./RotatingBadge";
import { SITE } from "@/config/site";

export const Hero = () => {
  const ref = useRef(null);
  const d = useRef(introDelay()).current;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 110, damping: 16 });
  const glowX = useTransform(mx, [-0.5, 0.5], ["30%", "70%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={ref} data-testid="hero-section" onMouseMove={onMove} onMouseLeave={onLeave} className="relative min-h-[100svh] overflow-hidden pt-28 sm:pt-36 pb-16">
      <motion.div aria-hidden="true" style={{ left: glowX }} className="pointer-events-none absolute top-[18%] -translate-x-1/2 w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full bg-[radial-gradient(circle,rgba(255,85,0,0.32),transparent_62%)] blur-2xl" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-6 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10 min-w-0">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: d, duration: 0.7 }} className="eyebrow flex items-center gap-3">
            <span className="relative flex w-2.5 h-2.5"><span className="absolute inset-0 rounded-full bg-[#FF5500] animate-ping" /><span className="relative w-2.5 h-2.5 rounded-full bg-[#FF5500]" /></span>
            Specialità allo spiedo · Dal 1998 · Volla
          </motion.p>
          <h1 data-testid="hero-title" className="mt-6 font-display uppercase leading-[0.84] tracking-[-0.045em] text-[10.5vw] sm:text-7xl lg:text-[4.25rem] xl:text-[5.4rem]">
            <MaskLines delay={d + 0.05} lines={[{ text: "Gira." }, { text: "Arrosta.", className: "text-outline" }, { text: "Spacca.", className: "text-[#FF5500]" }]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: d + 0.55, duration: 0.9, ease: SOFT }} className="mt-8 max-w-md text-base sm:text-lg text-zinc-300 leading-relaxed">
            Passione per lo spiedo dal 1998.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: d + 0.7, duration: 0.9, ease: SOFT }} className="mt-10 flex flex-wrap gap-3">
            <Link to="/menu" data-testid="hero-cta-menu" className="btn-primary group">
              Guarda il menù <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
            <a href={SITE.phoneHref} data-testid="hero-cta-call" className="btn-ghost group">
              <Phone className="w-4 h-4 transition-transform duration-300 group-hover:-rotate-12" /> Chiama e prenota
            </a>
          </motion.div>
        </motion.div>
        <HeroVisual d={d} rx={rx} ry={ry} imgY={imgY} />
      </div>
    </section>
  );
};

const HeroVisual = ({ d, rx, ry, imgY }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.92, y: 60 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ delay: d + 0.15, duration: 1.3, ease: SOFT }}
    className="lg:col-span-5 relative mx-auto w-full max-w-[420px] lg:max-w-none [perspective:1200px]"
  >
    <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
      <div data-testid="hero-image-frame" className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[2.5rem] border border-white/10 shadow-[0_40px_120px_-30px_rgba(255,85,0,0.55)]">
        <motion.video style={{ y: imgY }} src="/video/hero.mp4" poster="/images/hero-poster.jpg" autoPlay muted loop playsInline data-testid="hero-video" aria-label="Polli che girano sullo spiedo da Mister Pollo" className="absolute inset-0 w-full h-[118%] -top-[9%] object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0F]/80 via-transparent to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between [transform:translateZ(40px)]">
          <div>
            <p className="eyebrow !text-[#FFF4E8]/80">La specialità</p>
            <p className="font-display text-2xl sm:text-3xl uppercase leading-none mt-2">Pollo allo spiedo</p>
          </div>
        </div>
      </div>
      <div className="absolute -top-4 -right-2 sm:-right-6 rotate-[8deg] rounded-2xl bg-[#FFC72C] text-[#0D0D0F] px-4 py-3 font-display uppercase text-sm leading-tight shadow-xl [transform:translateZ(80px)_rotate(8deg)]">
        Dal<br />1998
      </div>
      <div className="absolute -right-3 sm:-right-8 -bottom-10 [transform:translateZ(60px)]">
        <RotatingBadge />
      </div>
    </motion.div>
  </motion.div>
);
